import type { CommentPaint } from "../../lib/canvasCommentColors";
import { anchorSpan, type ReadCanvasAnchor } from "./readCanvasAnchors";

/**
 * `readCanvasHighlights` — comment IDENTITY, carried from a resolved anchor all
 * the way to the engine's highlight layer.
 *
 * THE DEFECT THIS CLOSES. `<ReadCanvas>.reanchor` used to do
 * `rects.push(...surface.rangeRects(a.blockOrd, a.start, a.end))` into one flat
 * array and hand that to `setHighlights`. Three things died in that line:
 *  · the comment TYPE, so every span painted the engine's fallback amber and a
 *    request-for-change looked identical to a note — while their cards were red
 *    and blue two inches away;
 *  · the comment ID, so a click on painted text could not name what it hit and
 *    "click the highlight to open its thread" simply did not exist on canvas;
 *  · the per-comment RECTS, so a Review-Note flash had nothing to bloom.
 *
 * This module is the DOM-free seam that keeps all three — same discipline as
 * `readCanvasAnchors`: it takes a STRUCTURAL rect source, never the engine, so it
 * is fully testable in a runtime with no Canvas 2D context (happy-dom has none,
 * so `<ReadCanvas>` can never reach a live surface headlessly).
 *
 * RULE ZERO. The painting, the per-rect fill and the bloom animation are the
 * engine's (`HighlightRect.color`, `flashHighlights`). The colour POLICY is
 * `lib/canvasCommentColors`. What lives here is only the join between them.
 */

/** A page rect that carries its OWN fill — structurally the engine's
 *  `HighlightRect`. Kept generic so this module never imports the engine. */
export type ColoredRect<Rect> = Rect & { readonly color: string };

/** The id the PENDING selection band is painted under. Deliberately not a
 *  number, so it can never collide with a comment id. */
export const PENDING_ID = "pending";

/** The live "you are commenting on this" selection, before it becomes a comment. */
export interface PendingBand {
  readonly bands: readonly { blockOrd: number; start: number; end: number }[];
  /** The composer's current type pick — the preview's colour source. */
  readonly type: string;
}

/**
 * The anchors to PAINT: every resolved comment, plus the pending band when a
 * selection is live.
 *
 * The pending band is a synthetic {@link ReadCanvasAnchor} so it rides the SAME
 * planner, painter and colour policy every saved comment does — Rule Zero, no
 * second paint path. It is appended LAST, which makes it the innermost band
 * wherever a reviewer is selecting inside already-commented text; the click
 * hit-test therefore resolves against the SAVED anchors only, rather than
 * relying on that ordering.
 *
 * Its state is `exact` because a live selection is not a re-anchored quote — the
 * exact/moved confidence axis does not apply to it, and painting it `moved`
 * would tint the reviewer's own selection as though it had drifted.
 */
export function withPendingBand(
  anchors: readonly ReadCanvasAnchor[],
  pending: PendingBand | null,
): readonly ReadCanvasAnchor[] {
  const first = pending?.bands[0];
  if (!pending || !first) return anchors;
  return [
    ...anchors,
    {
      commentId: PENDING_ID,
      type: pending.type,
      bands: pending.bands,
      blockOrd: first.blockOrd,
      start: first.start,
      end: first.end,
      state: "exact",
    },
  ];
}

/**
 * WHICH band takes the glow. The POINTER wins over the gutter card, which wins
 * over the click — exactly the order the DOM expressed as `:hover` beating
 * `[data-linked-hover]` beating the resting rule.
 *
 * Stated as one function because the precedence is the whole behaviour: with the
 * three sources read in a different order at the two call sites (a full repaint
 * and a cheap recolour), the emphasis would flicker between them as the reviewer
 * moved between the paper and the rail.
 */
export function emphasisedId(
  hovered: string | null,
  linked: string | null,
  selected: string | null,
): string | null {
  return hovered ?? linked ?? selected;
}

/** The slice of `CanvasSurfaceHandle` a highlight plan needs. */
export interface RectSource<Rect> {
  rangeRects(blockOrd: number, start: number, end: number): readonly Rect[];
}

export interface HighlightPlan<Rect> {
  /** Every rect in the document, each carrying its own comment's fill — the
   *  array handed straight to `surface.setHighlights`. */
  readonly rects: readonly ColoredRect<Rect>[];
  /** Raw (un-tinted) rects per comment id. The flash paints its own wash over
   *  these, so they must NOT carry the resting fill. */
  readonly byComment: ReadonlyMap<string, readonly Rect[]>;
}

/**
 * Re-tint bands that have ALREADY been measured — the cheap path.
 *
 * `emphasisedCommentId` takes the glow (the DOM's `:hover` /
 * `[data-linked-hover]` rule); everyone else takes the resting wash. This does
 * NO geometry work, which is the whole point: hovering the document must not
 * re-run one wasm `rangeRects` per comment per pointer move. The DOM got this
 * for free from CSS `:hover`; on a canvas it costs exactly one recolour of a
 * cached array plus one overlay repaint, and only when the hovered comment
 * CHANGES.
 */
export function recolorHighlights<Rect extends object>(
  byComment: ReadonlyMap<string, readonly Rect[]>,
  anchors: readonly ReadCanvasAnchor[],
  paintFor: (anchor: ReadCanvasAnchor) => CommentPaint,
  emphasisedCommentId: string | null,
): readonly ColoredRect<Rect>[] {
  const out: ColoredRect<Rect>[] = [];
  for (const a of anchors) {
    const raw = byComment.get(a.commentId);
    if (!raw) continue;
    const paint = paintFor(a);
    const color = a.commentId === emphasisedCommentId ? paint.glow : paint.rest;
    for (const r of raw) out.push({ ...r, color });
  }
  return out;
}

/**
 * Ask the surface for every anchor's rects and tint each one with its comment's
 * fill — glow for `emphasisedCommentId`, rest for everyone else.
 *
 * Byte offsets are stable across relayout; PIXELS ARE NOT, so this full pass is
 * run on every re-anchor rather than cached. Pointer emphasis rides
 * {@link recolorHighlights} over the map this returns.
 */
export function planHighlights<Rect extends object>(
  source: RectSource<Rect>,
  anchors: readonly ReadCanvasAnchor[],
  paintFor: (anchor: ReadCanvasAnchor) => CommentPaint,
  emphasisedCommentId: string | null,
): HighlightPlan<Rect> {
  const byComment = new Map<string, readonly Rect[]>();
  for (const a of anchors) {
    // ONE MEASURE PER BAND. A comment anchored across blocks is N bands, and
    // asking only for the first one's rects painted a single band of ink over a
    // range the reviewer had drawn across a paragraph, a list and a table — the
    // model right, the raster wrong, which is the failure mode a state-only
    // assertion cannot see. A band with no glyphs (a blank paragraph inside the
    // range) contributes no rect, which is honest rather than missing.
    const raw: Rect[] = [];
    for (const band of a.bands) {
      raw.push(...source.rangeRects(band.blockOrd, band.start, band.end));
    }
    if (raw.length === 0) continue;
    byComment.set(a.commentId, raw);
  }
  return {
    rects: recolorHighlights(byComment, anchors, paintFor, emphasisedCommentId),
    byComment,
  };
}

/** A page-space point, as the engine's `pagePointAt` reports it. */
export interface HitPoint {
  readonly pageIndex: number;
  readonly xPt: number;
  readonly yPt: number;
}

/** A page-space band — structurally the engine's `PageRect`. */
export interface HitRect extends HitPoint {
  readonly wPt: number;
  readonly hPt: number;
}

function contains(r: HitRect, p: HitPoint): boolean {
  // HALF-OPEN on both axes, so two adjacent bands never both claim a boundary.
  return (
    r.pageIndex === p.pageIndex &&
    p.xPt >= r.xPt &&
    p.xPt < r.xPt + r.wPt &&
    p.yPt >= r.yPt &&
    p.yPt < r.yPt + r.hPt
  );
}

/**
 * Which comment's PAINTED BAND is under `point`, or `null` for anywhere else.
 *
 * WHY GEOMETRY AND NOT A BYTE OFFSET. The engine also exposes `positionAt`, the
 * caret hit test — but that one SNAPS: a press in a margin or below the last
 * line is deliberately rescued onto the nearest line, because that is right for
 * "put the caret where I clicked". It is wrong for "did the reader click ON this
 * highlight", where a click in empty space would snap onto text and open a
 * comment nowhere near the pointer. So the test is done against the very rects
 * that were PAINTED (`pagePointAt` → page space → containment), which is exact
 * by construction: what the reader sees is what they hit.
 *
 * INNERMOST WINS. When bands overlap — a reviewer commenting on one phrase
 * inside an already-commented sentence — the comment with the SHORTEST byte
 * range wins. Widest-wins would make the inner comment permanently unclickable,
 * which is the worse failure: the outer comment is still reachable somewhere
 * outside the inner one, the inner one would be reachable nowhere. Among EQUAL
 * ranges the LAST anchor wins — resolution follows the comment list, so that is
 * the most recently filed comment.
 */
export function commentIdAtPoint(
  anchors: readonly ReadCanvasAnchor[],
  rectsByComment: ReadonlyMap<string, readonly HitRect[]>,
  point: HitPoint | null,
): string | null {
  if (!point) return null;
  let best: ReadCanvasAnchor | null = null;
  for (const a of anchors) {
    const rects = rectsByComment.get(a.commentId);
    if (!rects?.some((r) => contains(r, point))) continue;
    // The span is the SUM of the bands, not `end - start`: for a cross-block
    // anchor those two offsets are in different blocks, so their difference is
    // not a width at all and "innermost wins" would be decided by arithmetic on
    // unrelated numbers.
    if (best === null || anchorSpan(a) <= anchorSpan(best)) best = a;
  }
  return best?.commentId ?? null;
}

/** Holds the cancel handle of the ONE bloom that may be running. */
export interface FlashController {
  /** Run `begin` (which starts the engine's flash and returns its cancel),
   *  cancelling whatever bloom was already running. */
  start(begin: () => () => void): void;
  /** Cancel the running bloom, if any. Idempotent. */
  cancel(): void;
}

/**
 * One bloom at a time.
 *
 * Two overlapping washes composite into a colour neither of them is, and the
 * second flash's fade would be fighting the first's — so a new flash always
 * supersedes. This owns NO motion of its own: it schedules no frame and no
 * timer (asserted), it only holds the engine's cancel.
 */
export function createFlashController(): FlashController {
  let cancelRunning: (() => void) | null = null;
  const cancel = (): void => {
    const c = cancelRunning;
    cancelRunning = null;
    c?.();
  };
  return {
    cancel,
    start(begin) {
      cancel();
      cancelRunning = begin();
    },
  };
}
